import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { contactKeys } from '@/lib/queryKeys';
import { apiPost, operationsClient } from '@/lib/api';
import type { Contact, CreateContactDto } from '@/types/contact';
import { normalizeContact } from './useContacts';

export function useCreateContact() {
  const queryClient = useQueryClient();

  return useApiMutation<Contact, CreateContactDto>({
    mutationFn: async (dto) => {
      const raw = await apiPost<Record<string, unknown>>(operationsClient, '/contacts', dto);
      return normalizeContact(raw);
    },
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      if (variables.notes) {
        void queryClient.invalidateQueries({ queryKey: contactKeys.notes(data.id) });
      }
      toast.success(`${data.firstName} ${data.lastName ?? ''}`.trim() + ' created');
      // NOT-138 — when the BE flags the primary phone as already in use,
      // surface a soft warning toast alongside the success. The operator
      // can still keep the new contact; the warning is informational.
      if (data.duplicatePhoneWarning) {
        const count = data.duplicatePhoneWarning.existingContactIds.length;
        toast.warning(
          data.duplicatePhoneWarning.message ||
            `Phone number already used by ${count} other contact${count === 1 ? '' : 's'}.`,
        );
      }
    },
  });
}
